/**
 * useScrollToFocusedAdhoc - shared cross-pane focus behaviour for the Questionnaire <-> Prioritization
 * split view. When `focusedKey` changes, it finds the element tagged `data-adhoc-fieldkey={focusedKey}`
 * inside `rootRef`, scrolls it into view within its scroll container, and applies a transient
 * `is-focus-flash` highlight. It no-ops when the key is null or the element isn't rendered in this
 * pane (e.g. the ad-hoc section is filtered out of the Questionnaire), so a click never throws.
 *
 * The hook only READS focus and scrolls - it never sets focus - so there is no feedback loop:
 * programmatic scroll does not fire onClick/onFocus. Mirrors the scroll pattern in SowPreviewPane.
 */
import * as React from 'react';

export function useScrollToFocusedAdhoc(
  focusedKey: string | null,
  rootRef: React.RefObject<HTMLElement>
): void {
  React.useEffect(() => {
    if (!focusedKey || !rootRef.current) return;
    const el = rootRef.current.querySelector<HTMLElement>(
      `[data-adhoc-fieldkey="${CSS.escape(focusedKey)}"]`
    );
    if (!el) return; // section filtered out / not in this pane -> no-op
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    el.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
    el.classList.add('is-focus-flash');
    const timer = window.setTimeout(() => el.classList.remove('is-focus-flash'), 1400);
    return () => {
      window.clearTimeout(timer);
      el.classList.remove('is-focus-flash');
    };
  }, [focusedKey, rootRef]);
}
